// src/components/formularios/FormularioEditorDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Box,
  Typography,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
  Paper,
  Autocomplete,
} from '@mui/material';
import gorilaImg from '../../imagenes/Gorilas/gorila_formdialog.png';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import {
  createFormulario,
  updateFormulario,
  getFormulario,
  getRespuestasIndividuales,
  type Formulario,
  type CreateFormularioDto,
  type CreatePreguntaDto,
  type CreateOpcionDto,
  type FormularioTipo,
} from '../../api/formularios';
import { useAuth } from '../../context/AuthContext';
import ConfirmExitFormularioDialog from './ConfirmExitFormularioDialog';

interface Props {
  open: boolean;
  formularioId?: number;
  // Optional template to prefill the dialog when creating a new formulario
  template?: Formulario | null;
  onClose: () => void;
  onSaved: () => void;
}

const TIPOS_PREGUNTA = [
  { value: 'puntuacion', label: 'Puntuación' },
  { value: 'texto_corto', label: 'Texto Corto' },
  { value: 'texto_largo', label: 'Texto Largo' },
  { value: 'seleccion_unica', label: 'Selección Única' },
  { value: 'seleccion_multiple', label: 'Selección Múltiple' },
  { value: 'fecha', label: 'Fecha' },
  { value: 'hora', label: 'Hora' },
] as const;

// Departamentos de staff (todos los departamentos excepto profesor, cliente, consultor)
const DEPARTAMENTOS_STAFF = [
  'att_familias',
  'att_alumnado',
  'att_profesorado',
  'facturacion',
  'hr',
  'admisiones',
] as const;

const DEPARTAMENTOS_DISPONIBLES = [
  { value: 'staff', label: 'Staff (todos los departamentos)' },
  { value: 'general', label: 'Solo Admin' },
  { value: 'att_familias', label: 'Atención a Familias' },
  { value: 'att_alumnado', label: 'Atención al Alumnado' },
  { value: 'att_profesorado', label: 'Atención al Profesorado' },
  { value: 'facturacion', label: 'Facturación' },
  { value: 'hr', label: 'Recursos Humanos' },
  { value: 'admisiones', label: 'Admisiones' },
  { value: 'profesor', label: 'Profesores' },
] as const;

export default function FormularioEditorDialog({ open, formularioId, template, onClose, onSaved }: Props) {
  const { user } = useAuth();
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [estado, setEstado] = useState<'borrador' | 'activo' | 'desactivado' | 'cerrado'>(
    'activo',
  );
  const [tipoFormulario, setTipoFormulario] = useState<FormularioTipo>('general');
  const [respuestasAnonimas, setRespuestasAnonimas] = useState(false);
  const [fechaDesde, setFechaDesde] = useState<Dayjs | null>(null);
  const [fechaHasta, setFechaHasta] = useState<Dayjs | null>(null);
  const [departamentosSeleccionados, setDepartamentosSeleccionados] = useState<string[]>([]);
  const [departamento, setDepartamento] = useState<string | null>(null);
  const [departamentosGeneralSeleccionados, setDepartamentosGeneralSeleccionados] = useState<string[]>([]);
  const [esGeneral, setEsGeneral] = useState(false);
  const [fechaLimite, setFechaLimite] = useState<Dayjs | null>(null);
  const [fechaEnvioProgramada, setFechaEnvioProgramada] = useState<Dayjs | null>(null);
  const [esPlantilla, setEsPlantilla] = useState(false);
  const [preguntas, setPreguntas] = useState<Array<CreatePreguntaDto & { tempId?: number; originalId?: number }>>([]);
  const [originalPreguntaIds, setOriginalPreguntaIds] = useState<number[]>([]);
  const [respuestasCount, setRespuestasCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Determinar tipos de formulario permitidos según rol
  const tiposPermitidos = React.useMemo(() => {
    const tipos: Array<{ value: FormularioTipo; label: string; disabled?: boolean }> = [
      { value: 'general', label: 'General' },
    ];

    if (user?.roles?.includes('admin')) {
      // Admin puede crear todos los tipos
      tipos.push(
        { value: 'profesor_alumnos', label: 'Profesor → Alumnos' },
        { value: 'manager_departamento', label: 'Manager → Departamento' },
        { value: 'evaluacion_managers', label: 'Evaluación de Managers' },
      );
    } else if (user?.roles?.includes('hr')) {
      // Manager HR solo puede crear estos tipos
      tipos.push(
        { value: 'manager_departamento', label: 'Manager → Departamento' },
        { value: 'evaluacion_managers', label: 'Evaluación de Managers' },
      );
    } else if (user?.roles?.includes('att_alumnado')) {
      // Manager att_alumnado solo puede crear este tipo
      tipos.push({ value: 'profesor_alumnos', label: 'Rendimiento de Alumnos' });
    }

    return tipos;
  }, [user]);

  useEffect(() => {
    if (open) {
      if (formularioId) {
        loadFormulario();
      } else if (template) {
        prefillFromTemplate(template);
      } else {
        resetForm();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formularioId, template]);

  const loadFormulario = async () => {
    if (!formularioId) return;
    setLoadingForm(true);
    try {
      const form = await getFormulario(formularioId);
      setTitulo(form.titulo);
      setDescripcion(form.descripcion || '');
      setEstado(form.estado as any);
      setTipoFormulario(form.tipoFormulario || 'general');
      setRespuestasAnonimas(form.respuestasAnonimas || false);
      setFechaDesde(form.fechaDesde ? dayjs(form.fechaDesde) : null);
      setFechaHasta(form.fechaHasta ? dayjs(form.fechaHasta) : null);
      // Para manager_departamento, si hay departamento único, convertirlo a array
      if (form.tipoFormulario === 'manager_departamento') {
        if (form.departamentosSeleccionados && form.departamentosSeleccionados.length > 0) {
          setDepartamentosSeleccionados(form.departamentosSeleccionados);
        } else if (form.departamento) {
          setDepartamentosSeleccionados([form.departamento]);
        } else {
          setDepartamentosSeleccionados([]);
        }
        setDepartamento(null); // No usar departamento único para manager_departamento
      } else {
        setDepartamentosSeleccionados(form.departamentosSeleccionados || []);
        setDepartamento(form.departamento ?? null);
        // Para tipo general, convertir departamento único a array para el Autocomplete
        if (form.tipoFormulario === 'general' || !form.tipoFormulario) {
          if (form.departamento) {
            // Si hay un departamento, verificar si es uno de los de staff
            if (DEPARTAMENTOS_STAFF.includes(form.departamento as any)) {
              // Verificar si todos los departamentos de staff están seleccionados (equivalente a "Staff")
              setDepartamentosGeneralSeleccionados([form.departamento]);
            } else {
              setDepartamentosGeneralSeleccionados([form.departamento]);
            }
            setEsGeneral(false);
          } else {
            // Si es null, significa "General (solo admin)"
            setDepartamentosGeneralSeleccionados([]);
            setEsGeneral(true);
          }
        }
      }
      setFechaLimite(form.fechaLimite ? dayjs(form.fechaLimite) : null);
      setFechaEnvioProgramada(form.fechaEnvioProgramada ? dayjs(form.fechaEnvioProgramada) : null);
      setEsPlantilla(form.esPlantilla);
      if (form.preguntas) {
        const sorted = form.preguntas
          .sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0))
          .map((p) => ({
            orden: Number(p.orden) || 1,
            tipo: p.tipo,
            pregunta: p.pregunta || '',
            requerida: p.requerida !== undefined ? Boolean(p.requerida) : true,
            escalaMin:
              p.escalaMin !== undefined && p.escalaMin !== null && !isNaN(Number(p.escalaMin))
                ? Number(p.escalaMin)
                : undefined,
            escalaMax:
              p.escalaMax !== undefined && p.escalaMax !== null && !isNaN(Number(p.escalaMax))
                ? Number(p.escalaMax)
                : undefined,
            limiteCaracteres:
              p.limiteCaracteres !== undefined &&
              p.limiteCaracteres !== null &&
              !isNaN(Number(p.limiteCaracteres))
                ? Number(p.limiteCaracteres)
                : undefined,
            condicionesJson: p.condicionesJson,
            opciones: p.opciones
              ?.sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0))
              .map((o, idx) => ({
                texto: o.texto || '',
                orden: Number(o.orden) || idx + 1,
              })),
            // Keep the original pregunta id to avoid accidental deletion when saving
            originalId: p.id,
          }));

        setPreguntas(sorted);
        setOriginalPreguntaIds(sorted.map((s) => s.originalId!).filter(Boolean) as number[]);

        // Load respuestas count to prevent deleting preguntas with respuestas
        try {
          const resp = await getRespuestasIndividuales(formularioId!, { limit: 1 });
          setRespuestasCount(resp.total || 0);
        } catch (e) {
          // ignore
        }
      }
    } catch (error: any) {
      enqueueSnackbar('Error al cargar formulario', { variant: 'error' });
    } finally {
      setLoadingForm(false);
    }
  };

  const prefillFromTemplate = (form: Formulario) => {
    setTitulo(form.titulo);
    setDescripcion(form.descripcion || '');
    // When creating from template, default to 'borrador' so user can edit
    setEstado('borrador');
    setTipoFormulario(form.tipoFormulario || 'general');
    setRespuestasAnonimas(form.respuestasAnonimas || false);
    setFechaDesde(form.fechaDesde ? dayjs(form.fechaDesde) : null);
    setFechaHasta(form.fechaHasta ? dayjs(form.fechaHasta) : null);

    if (form.tipoFormulario === 'manager_departamento') {
      if (form.departamentosSeleccionados && form.departamentosSeleccionados.length > 0) {
        setDepartamentosSeleccionados(form.departamentosSeleccionados);
      } else if (form.departamento) {
        setDepartamentosSeleccionados([form.departamento]);
      } else {
        setDepartamentosSeleccionados([]);
      }
      setDepartamento(null);
    } else {
      setDepartamentosSeleccionados(form.departamentosSeleccionados || []);
      setDepartamento(form.departamento ?? null);
      if (form.tipoFormulario === 'general' || !form.tipoFormulario) {
        if (form.departamento) {
          setDepartamentosGeneralSeleccionados([form.departamento]);
          setEsGeneral(false);
        } else {
          setDepartamentosGeneralSeleccionados([]);
          setEsGeneral(true);
        }
      }
    }

    setFechaLimite(form.fechaLimite ? dayjs(form.fechaLimite) : null);
    setFechaEnvioProgramada(form.fechaEnvioProgramada ? dayjs(form.fechaEnvioProgramada) : null);
    setEsPlantilla(false); // When using template to create new, default esPlantilla to false

    if (form.preguntas) {
      setPreguntas(
        form.preguntas
          .sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0))
          .map((p) => ({
            orden: Number(p.orden) || 1,
            tipo: p.tipo,
            pregunta: p.pregunta || '',
            requerida: p.requerida !== undefined ? Boolean(p.requerida) : true,
            escalaMin:
              p.escalaMin !== undefined && p.escalaMin !== null && !isNaN(Number(p.escalaMin))
                ? Number(p.escalaMin)
                : undefined,
            escalaMax:
              p.escalaMax !== undefined && p.escalaMax !== null && !isNaN(Number(p.escalaMax))
                ? Number(p.escalaMax)
                : undefined,
            limiteCaracteres:
              p.limiteCaracteres !== undefined && p.limiteCaracteres !== null && !isNaN(Number(p.limiteCaracteres))
                ? Number(p.limiteCaracteres)
                : undefined,
            condicionesJson: p.condicionesJson,
            opciones: p.opciones
              ?.sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0))
              .map((o, idx) => ({ texto: o.texto || '', orden: Number(o.orden) || idx + 1 })),
          })),
      );
    } else {
      setPreguntas([]);
    }
  };
  const resetForm = () => {
    setTitulo('');
    setDescripcion('');
    setEstado('activo');
    setTipoFormulario('general');
    setRespuestasAnonimas(false);
    setFechaDesde(null);
    setFechaHasta(null);
    setDepartamentosSeleccionados([]);
    setDepartamento(null);
    setDepartamentosGeneralSeleccionados([]);
    setEsGeneral(false);
    setFechaLimite(null);
    setFechaEnvioProgramada(null);
    setEsPlantilla(false);
    setPreguntas([]);
  };

  const addPregunta = () => {
    const newOrden = preguntas.length > 0 ? Math.max(...preguntas.map((p) => p.orden)) + 1 : 1;
    setPreguntas([
      ...preguntas,
      {
        orden: newOrden,
        tipo: 'texto_corto',
        pregunta: '',
        requerida: true,
        tempId: Date.now(),
      },
    ]);
  };

  const removePregunta = (index: number) => {
    const pregunta = preguntas[index];
    // If this pregunta existed in the original form and there are respuestas, block deletion
    if (pregunta.originalId && respuestasCount > 0) {
      enqueueSnackbar('No se puede eliminar una pregunta que ya tiene respuestas. Elimina las respuestas primero o crea una copia del formulario.', { variant: 'warning' });
      return;
    }
    setPreguntas(preguntas.filter((_, i) => i !== index));
  };

  const updatePregunta = (index: number, updates: Partial<CreatePreguntaDto>) => {
    setPreguntas(preguntas.map((p, i) => (i === index ? { ...p, ...updates } : p)));
  };

  const addOpcion = (preguntaIndex: number) => {
    const pregunta = preguntas[preguntaIndex];
    const opciones = pregunta.opciones || [];
    const newOrden = opciones.length > 0 ? Math.max(...opciones.map((o) => o.orden)) + 1 : 1;
    updatePregunta(preguntaIndex, {
      opciones: [...opciones, { texto: '', orden: newOrden }],
    });
  };

  const removeOpcion = (preguntaIndex: number, opcionIndex: number) => {
    const pregunta = preguntas[preguntaIndex];
    const opciones = pregunta.opciones || [];
    updatePregunta(preguntaIndex, {
      opciones: opciones.filter((_, i) => i !== opcionIndex),
    });
  };

  const updateOpcion = (
    preguntaIndex: number,
    opcionIndex: number,
    updates: Partial<CreateOpcionDto>,
  ) => {
    const pregunta = preguntas[preguntaIndex];
    const opciones = pregunta.opciones || [];
    updatePregunta(preguntaIndex, {
      opciones: opciones.map((o, i) => (i === opcionIndex ? { ...o, ...updates } : o)),
    });
  };

  const handleSubmit = async () => {
    if (!titulo.trim()) {
      enqueueSnackbar('El título es obligatorio', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const payload: CreateFormularioDto = {
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || undefined,
        estado,
        tipoFormulario: tipoFormulario !== 'general' ? tipoFormulario : undefined,
        respuestasAnonimas: respuestasAnonimas || undefined,
        fechaDesde: fechaDesde ? fechaDesde.toISOString() : undefined,
        fechaHasta: fechaHasta ? fechaHasta.toISOString() : undefined,
        departamentosSeleccionados:
          tipoFormulario === 'manager_departamento' || tipoFormulario === 'evaluacion_managers'
            ? departamentosSeleccionados.length > 0
              ? departamentosSeleccionados.filter((d) => d !== 'staff') // Filtrar 'staff' antes de enviar
              : undefined
            : undefined,
        departamento:
          tipoFormulario === 'manager_departamento'
            ? null // Para manager_departamento, no usar departamento único
            : departamento || null,
        fechaLimite: fechaLimite ? fechaLimite.toISOString() : undefined,
        fechaEnvioProgramada: fechaEnvioProgramada ? fechaEnvioProgramada.toISOString() : undefined,
        esPlantilla,
        preguntas: preguntas.map((p) => {
          const preguntaData: any = {
            orden: Number(p.orden) || 1,
            tipo: p.tipo,
            pregunta: p.pregunta.trim(),
            requerida: p.requerida !== undefined ? Boolean(p.requerida) : true,
          };

          // Solo incluir campos numéricos si tienen valores válidos y son números
          if (p.escalaMin !== undefined && p.escalaMin !== null && !isNaN(Number(p.escalaMin))) {
            preguntaData.escalaMin = Number(p.escalaMin);
          }
          if (p.escalaMax !== undefined && p.escalaMax !== null && !isNaN(Number(p.escalaMax))) {
            preguntaData.escalaMax = Number(p.escalaMax);
          }
          if (
            p.limiteCaracteres !== undefined &&
            p.limiteCaracteres !== null &&
            !isNaN(Number(p.limiteCaracteres))
          ) {
            preguntaData.limiteCaracteres = Number(p.limiteCaracteres);
          }

          if (p.condicionesJson) {
            preguntaData.condicionesJson = p.condicionesJson;
          }

          if (p.opciones && p.opciones.length > 0) {
            preguntaData.opciones = p.opciones
              .filter((o) => o.texto && o.texto.trim())
              .map((o, idx) => ({
                texto: o.texto.trim(),
                orden: Number(o.orden) || idx + 1,
              }));
          }

          return preguntaData;
        }),
      };

      // Prevent deleting preguntas that had respuestas
      if (formularioId) {
        const removedOriginalIds = originalPreguntaIds.filter(
          (id) => !preguntas.some((p) => p.originalId === id),
        );
        if (removedOriginalIds.length > 0 && respuestasCount > 0) {
          enqueueSnackbar('No se puede eliminar preguntas que ya tienen respuestas. Elimina las respuestas primero o crea una copia del formulario.', { variant: 'warning' });
          setLoading(false);
          return;
        }
      }

      let savedForm: any = null;
      if (formularioId) {
        savedForm = await updateFormulario(formularioId, payload);
        enqueueSnackbar('Formulario actualizado', { variant: 'success' });
      } else {
        savedForm = await createFormulario(payload);
        enqueueSnackbar('Formulario creado', { variant: 'success' });
      }

      // Notify plantillas dropdown to reload immediately
      try {
        window.dispatchEvent(new CustomEvent('plantillas:changed', { detail: { id: savedForm?.id } }));
      } catch (e) {
        // ignore
      }

      onSaved();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al guardar formulario', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Manejar click en cancelar
  const handleCancelClick = () => {
    // Si es un formulario nuevo y tiene algún dato, mostrar confirmación
    if (!formularioId && (titulo.trim() || descripcion.trim() || preguntas.length > 0)) {
      setConfirmExitOpen(true);
    } else {
      onClose();
    }
  };

  // Cerrar sin guardar
  const handleDiscardChanges = () => {
    setConfirmExitOpen(false);
    onClose();
  };

  // Guardar como borrador
  const handleSaveDraft = async () => {
    if (!titulo.trim()) {
      enqueueSnackbar('El título es obligatorio para guardar como borrador', {
        variant: 'warning',
      });
      return;
    }

    setLoading(true);
    try {
      const payload: CreateFormularioDto = {
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || undefined,
        estado: 'borrador', // Forzar estado borrador
        tipoFormulario: tipoFormulario !== 'general' ? tipoFormulario : undefined,
        respuestasAnonimas: respuestasAnonimas || undefined,
        fechaDesde: fechaDesde ? fechaDesde.toISOString() : undefined,
        fechaHasta: fechaHasta ? fechaHasta.toISOString() : undefined,
        departamentosSeleccionados:
          tipoFormulario === 'manager_departamento' || tipoFormulario === 'evaluacion_managers'
            ? departamentosSeleccionados.length > 0
              ? departamentosSeleccionados.filter((d) => d !== 'staff')
              : undefined
            : undefined,
        departamento: tipoFormulario === 'manager_departamento' ? null : departamento || null,
        fechaLimite: fechaLimite ? fechaLimite.toISOString() : undefined,
        fechaEnvioProgramada: fechaEnvioProgramada ? fechaEnvioProgramada.toISOString() : undefined,
        esPlantilla,
        preguntas: preguntas.map((p) => {
          const preguntaData: any = {
            orden: Number(p.orden) || 1,
            tipo: p.tipo,
            pregunta: p.pregunta.trim(),
            requerida: p.requerida !== undefined ? Boolean(p.requerida) : true,
          };

          if (p.escalaMin !== undefined && p.escalaMin !== null && !isNaN(Number(p.escalaMin))) {
            preguntaData.escalaMin = Number(p.escalaMin);
          }
          if (p.escalaMax !== undefined && p.escalaMax !== null && !isNaN(Number(p.escalaMax))) {
            preguntaData.escalaMax = Number(p.escalaMax);
          }
          if (
            p.limiteCaracteres !== undefined &&
            p.limiteCaracteres !== null &&
            !isNaN(Number(p.limiteCaracteres))
          ) {
            preguntaData.limiteCaracteres = Number(p.limiteCaracteres);
          }

          if (p.condicionesJson) {
            preguntaData.condicionesJson = p.condicionesJson;
          }

          if (p.opciones && p.opciones.length > 0) {
            preguntaData.opciones = p.opciones
              .filter((o) => o.texto && o.texto.trim())
              .map((o, idx) => ({
                texto: o.texto.trim(),
                orden: Number(o.orden) || idx + 1,
              }));
          }

          return preguntaData;
        }),
      };

      await createFormulario(payload);
      enqueueSnackbar('Formulario guardado como borrador', { variant: 'success' });
      setConfirmExitOpen(false);
      onSaved();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al guardar borrador', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingForm) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(8px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'visible',
            ml: { xs: 0, md: '60px' },
          },
        }}
      >
        {/* Gorila lateral */}
        <Box
          component="img"
          src={gorilaImg}
          alt="Gorila"
          sx={{
            position: 'absolute',
            left: 0,
            top: { xs: '10px', sm: '-10px' },
            transform: 'translateX(-100%)',
            width: { xs: '70px', sm: '100px', md: '120px' },
            height: 'auto',
            zIndex: 1,
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <DialogTitle
          sx={{
            backgroundColor: '#000',
            color: '#fff',
            fontWeight: 'bold',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Box component="span">{formularioId ? 'Editar Formulario' : 'Nuevo Formulario'}</Box>
            {!formularioId && template && (
              <Chip
                label={`Creado a partir de plantilla`}
                size="small"
                sx={{ backgroundColor: '#F7E463', color: '#000', fontWeight: 'bold' }}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#fff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography>Cargando formulario...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(8px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'visible',
            ml: { xs: 0, md: '60px' },
          },
        }}
      >
        {/* Gorila lateral */}
        <Box
          component="img"
          src={gorilaImg}
          alt="Gorila"
          sx={{
            position: 'absolute',
            left: 0,
            top: { xs: '10px', sm: '-10px' },
            transform: 'translateX(-100%)',
            width: { xs: '70px', sm: '100px', md: '120px' },
            height: 'auto',
            zIndex: 1,
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <DialogTitle
          sx={{
            backgroundColor: '#000',
            color: '#fff',
            fontWeight: 'bold',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Box component="span">{formularioId ? 'Editar Formulario' : 'Nuevo Formulario'}</Box>
            {!formularioId && template && (
              <Chip
                label={`Creado a partir de plantilla`}
                size="small"
                sx={{ backgroundColor: '#F7E463', color: '#000', fontWeight: 'bold' }}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#fff' }}>
          <Stack spacing={3}>
            <TextField
              label="Título *"
              fullWidth
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
            />
            <TextField
              label="Descripción"
              fullWidth
              multiline
              rows={2}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Tipo de Formulario</InputLabel>
                <Select
                  value={tipoFormulario}
                  onChange={(e) => {
                    const nuevoTipo = e.target.value as FormularioTipo;
                    setTipoFormulario(nuevoTipo);
                    // Resetear campos específicos del tipo
                    if (nuevoTipo === 'general') {
                      setFechaDesde(null);
                      setFechaHasta(null);
                      setDepartamentosSeleccionados([]);
                      setDepartamentosGeneralSeleccionados([]);
                      setDepartamento(null);
                      setEsGeneral(false);
                      setRespuestasAnonimas(false);
                    } else if (nuevoTipo === 'profesor_alumnos') {
                      setDepartamentosSeleccionados([]);
                      setDepartamento(null);
                      setDepartamentosGeneralSeleccionados([]);
                      setEsGeneral(false);
                    } else if (nuevoTipo === 'manager_departamento') {
                      setFechaDesde(null);
                      setFechaHasta(null);
                      setDepartamentosSeleccionados([]);
                      setDepartamento(null);
                      setDepartamentosGeneralSeleccionados([]);
                      setEsGeneral(false);
                    } else if (nuevoTipo === 'evaluacion_managers') {
                      setFechaDesde(null);
                      setFechaHasta(null);
                      setDepartamento(null);
                      setDepartamentosGeneralSeleccionados([]);
                      setEsGeneral(false);
                    }
                  }}
                  label="Tipo de Formulario"
                  disabled={tiposPermitidos.length === 1}
                >
                  {tiposPermitidos.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value} disabled={tipo.disabled}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={estado}
                  onChange={(e) => setEstado(e.target.value as any)}
                  label="Estado"
                >
                  <MenuItem value="borrador">Borrador</MenuItem>
                  <MenuItem value="activo">Activo</MenuItem>
                  <MenuItem value="desactivado">Desactivado</MenuItem>
                  <MenuItem value="cerrado">Cerrado</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                sx={{ flex: 1, ml: 0 }}
                control={
                  <Checkbox
                    checked={esPlantilla}
                    onChange={(e) => setEsPlantilla(e.target.checked)}
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': { color: 'primary.main' },
                    }}
                  />
                }
                label="Guardar como plantilla"
              />
            </Stack>
            {tiposPermitidos.length === 1 && (
              <Typography variant="caption" color="text.secondary">
                Solo puedes crear formularios de tipo "{tiposPermitidos[0].label}" con tu rol actual
              </Typography>
            )}

            {/* Selector de departamento - múltiple para manager_departamento, único para otros */}
            {tipoFormulario === 'manager_departamento' ? (
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Departamentos</InputLabel>
                  <Select
                    multiple
                    value={departamentosSeleccionados}
                    onChange={(e) => {
                      const nuevos = e.target.value as string[];
                      // Si se selecciona "staff", reemplazar con todos los departamentos
                      if (nuevos.includes('staff')) {
                        setDepartamentosSeleccionados([...DEPARTAMENTOS_STAFF]);
                      } else {
                        setDepartamentosSeleccionados(nuevos);
                      }
                    }}
                    label="Departamentos"
                    renderValue={(selected) => {
                      if (
                        selected.length === DEPARTAMENTOS_STAFF.length &&
                        DEPARTAMENTOS_STAFF.every((dept) => selected.includes(dept))
                      ) {
                        return 'Todos los departamentos (Staff)';
                      }
                      return (selected as string[]).join(', ');
                    }}
                  >
                    <MenuItem value="staff">
                      <strong>Todos los departamentos (Staff)</strong>
                    </MenuItem>
                    <MenuItem disabled>
                      <em>──────────</em>
                    </MenuItem>
                    <MenuItem value="att_familias">Atención a Familias</MenuItem>
                    <MenuItem value="att_alumnado">Atención al Alumnado</MenuItem>
                    <MenuItem value="att_profesorado">Atención al Profesorado</MenuItem>
                    <MenuItem value="facturacion">Facturación</MenuItem>
                    <MenuItem value="hr">Recursos Humanos</MenuItem>
                    <MenuItem value="admisiones">Admisiones</MenuItem>
                  </Select>
                </FormControl>
                {departamentosSeleccionados.length === DEPARTAMENTOS_STAFF.length &&
                  DEPARTAMENTOS_STAFF.every((dept) =>
                    departamentosSeleccionados.includes(dept),
                  ) && (
                    <Typography variant="caption" color="primary">
                      ✓ Seleccionados todos los departamentos de staff
                    </Typography>
                  )}
              </Stack>
            ) : (
              <Autocomplete
                multiple
                options={DEPARTAMENTOS_DISPONIBLES}
                getOptionLabel={(option) => option.label}
                slotProps={{
                  popper: {
                    placement: 'bottom-start',
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 4],
                        },
                      },
                    ],
                  },
                }}
                value={DEPARTAMENTOS_DISPONIBLES.filter((dept) => {
                  if (dept.value === 'general') {
                    return esGeneral && departamentosGeneralSeleccionados.length === 0 && departamento === null;
                  }
                  if (dept.value === 'staff') {
                    return (
                      !esGeneral &&
                      departamentosGeneralSeleccionados.length === DEPARTAMENTOS_STAFF.length &&
                      DEPARTAMENTOS_STAFF.every((d) => departamentosGeneralSeleccionados.includes(d))
                    );
                  }
                  return !esGeneral && departamentosGeneralSeleccionados.includes(dept.value as any);
                })}
                onChange={(_, newValue) => {
                  const nuevosValores: string[] = newValue.map((dept) => dept.value);
                  
                  // Si se selecciona "general", limpiar todo y establecer como null
                  if (nuevosValores.includes('general')) {
                    setDepartamentosGeneralSeleccionados([]);
                    setDepartamento(null);
                    setEsGeneral(true);
                    return;
                  }
                  
                  // Si se estaba en modo "general" y ahora no está en nuevosValores, salir del modo general
                  if (esGeneral && !nuevosValores.includes('general')) {
                    setEsGeneral(false);
                  }
                  
                  // Filtrar valores especiales (staff y general) para obtener solo departamentos reales
                  // Excluir profesor si staff está seleccionado, ya que staff no incluye profesores
                  const seleccionandoStaff = nuevosValores.includes('staff');
                  let departamentosReales = nuevosValores.filter((r) => r !== 'staff' && r !== 'general');
                  
                  // Si staff está seleccionado, excluir profesor de los departamentos reales
                  if (seleccionandoStaff) {
                    departamentosReales = departamentosReales.filter((d) => d !== 'profesor');
                  }
                  
                  // Verificar si se tenía staff antes
                  const teniaStaff = departamentosGeneralSeleccionados.length === DEPARTAMENTOS_STAFF.length &&
                    DEPARTAMENTOS_STAFF.every((d) => departamentosGeneralSeleccionados.includes(d));
                  
                  if (seleccionandoStaff && !teniaStaff) {
                    // Se está seleccionando staff por primera vez - expandir a todos los departamentos de staff (sin profesores)
                    setDepartamentosGeneralSeleccionados([...DEPARTAMENTOS_STAFF]);
                    setDepartamento(null);
                    setEsGeneral(false);
                  } else if (!seleccionandoStaff && teniaStaff) {
                    // Se está deseleccionando staff (se hizo clic en la X) - limpiar todo
                    setDepartamentosGeneralSeleccionados(departamentosReales);
                    setDepartamento(departamentosReales.length > 0 ? departamentosReales[0] : null);
                    setEsGeneral(false);
                  } else if (seleccionandoStaff && teniaStaff) {
                    // Staff ya estaba seleccionado y sigue seleccionado - mantener solo staff (sin profesor)
                    // Pero si hay otros departamentos seleccionados además de staff, mantenerlos
                    if (departamentosReales.length > 0) {
                      setDepartamentosGeneralSeleccionados(departamentosReales);
                      setDepartamento(departamentosReales[0]);
                    } else {
                      setDepartamentosGeneralSeleccionados([...DEPARTAMENTOS_STAFF]);
                      setDepartamento(null);
                    }
                    setEsGeneral(false);
                  } else {
                    // Caso normal: selección/deselección de departamentos individuales (sin staff)
                    setDepartamentosGeneralSeleccionados(departamentosReales);
                    setDepartamento(departamentosReales.length > 0 ? departamentosReales[0] : null);
                    setEsGeneral(false);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departamento"
                    placeholder={esGeneral ? 'Solo Admin' : ''}
                  />
                )}
                renderTags={(value, getTagProps) => {
                  // Ordenar: primero Staff, luego Admin, luego el resto
                  const staffOption = value.find((dept) => dept.value === 'staff');
                  const generalOption = value.find((dept) => dept.value === 'general');
                  const otrosDeptos = value.filter(
                    (dept) => dept.value !== 'staff' && dept.value !== 'general'
                  );
                  
                  // Verificar si Staff está completamente seleccionado (todos los departamentos de staff)
                  const teniaStaff = departamentosGeneralSeleccionados.length === DEPARTAMENTOS_STAFF.length &&
                    DEPARTAMENTOS_STAFF.every((d) => departamentosGeneralSeleccionados.includes(d));
                  
                  // Calcular índices correctamente
                  let currentIndex = 0;
                  
                  // Si Staff está completamente seleccionado, solo mostrar Staff con índice 0
                  if (staffOption && teniaStaff) {
                    const tagProps = getTagProps({ index: 0 });
                    // Asegurar que el chip tenga el handler de eliminación
                    return (
                      <Chip 
                        label={staffOption.label} 
                        {...tagProps}
                        onDelete={tagProps.onDelete || (() => {
                          // Handler manual si getTagProps no lo proporciona
                          const newValue = value.filter((dept) => dept.value !== 'staff');
                          // Simular el onChange
                          const nuevosValores: string[] = newValue.map((dept) => dept.value);
                          const departamentosReales = nuevosValores.filter((r) => r !== 'general');
                          setDepartamentosGeneralSeleccionados(departamentosReales);
                          setDepartamento(departamentosReales.length > 0 ? departamentosReales[0] : null);
                          setEsGeneral(false);
                        })}
                      />
                    );
                  }
                  
                  // Caso normal: mostrar todos los chips en orden
                  return (
                    <>
                      {staffOption && !teniaStaff && (
                        <Chip 
                          label={staffOption.label} 
                          {...getTagProps({ index: currentIndex++ })} 
                        />
                      )}
                      {generalOption && (
                        <Chip 
                          label={generalOption.label} 
                          {...getTagProps({ index: currentIndex++ })} 
                        />
                      )}
                      {/* Solo mostrar otros departamentos si Staff NO está completamente seleccionado */}
                      {!teniaStaff && otrosDeptos.map((dept) => {
                        const tagProps = getTagProps({ index: currentIndex++ });
                        return <Chip {...tagProps} label={dept.label} />;
                      })}
                    </>
                  );
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) => {
                    const searchText = params.inputValue.toLowerCase();
                    const label = option.label.toLowerCase();
                    return label.includes(searchText);
                  });
                  return filtered;
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                fullWidth
              />
            )}
            {((departamento === null || departamentosGeneralSeleccionados.length === 0) &&
              tipoFormulario !== 'manager_departamento' &&
              tipoFormulario === 'general' &&
              user?.roles?.includes('admin')) && (
                <Typography variant="caption" color="text.secondary">
                  Los formularios generales (sin departamento) solo son visibles para
                  administradores
                </Typography>
              )}

            {/* Campos específicos según tipo de formulario */}
            {tipoFormulario === 'profesor_alumnos' && (
              <Stack direction="row" spacing={2}>
                <DateTimePicker
                  label="Fecha Desde"
                  value={fechaDesde}
                  onChange={(newValue) => setFechaDesde(newValue)}
                  format="DD/MM/YYYY HH:mm"
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DateTimePicker
                  label="Fecha Hasta"
                  value={fechaHasta}
                  onChange={(newValue) => setFechaHasta(newValue)}
                  format="DD/MM/YYYY HH:mm"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>
            )}

            {tipoFormulario === 'evaluacion_managers' && (
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Departamentos</InputLabel>
                  <Select
                    multiple
                    value={departamentosSeleccionados}
                    onChange={(e) => {
                      const nuevos = e.target.value as string[];
                      // Si se selecciona "staff", reemplazar con todos los departamentos
                      if (nuevos.includes('staff')) {
                        setDepartamentosSeleccionados([...DEPARTAMENTOS_STAFF]);
                      } else {
                        setDepartamentosSeleccionados(nuevos);
                      }
                    }}
                    label="Departamentos"
                    renderValue={(selected) => {
                      if (
                        selected.length === DEPARTAMENTOS_STAFF.length &&
                        DEPARTAMENTOS_STAFF.every((dept) => selected.includes(dept))
                      ) {
                        return 'Todos los departamentos (Staff)';
                      }
                      return (selected as string[]).join(', ');
                    }}
                  >
                    <MenuItem value="staff">
                      <strong>Todos los departamentos (Staff)</strong>
                    </MenuItem>
                    <MenuItem disabled>
                      <em>──────────</em>
                    </MenuItem>
                    <MenuItem value="att_familias">Atención a Familias</MenuItem>
                    <MenuItem value="att_alumnado">Atención al Alumnado</MenuItem>
                    <MenuItem value="att_profesorado">Atención al Profesorado</MenuItem>
                    <MenuItem value="facturacion">Facturación</MenuItem>
                    <MenuItem value="hr">Recursos Humanos</MenuItem>
                    <MenuItem value="admisiones">Admisiones</MenuItem>
                  </Select>
                </FormControl>
                {departamentosSeleccionados.length === DEPARTAMENTOS_STAFF.length &&
                  DEPARTAMENTOS_STAFF.every((dept) =>
                    departamentosSeleccionados.includes(dept),
                  ) && (
                    <Typography variant="caption" color="primary">
                      ✓ Seleccionados todos los departamentos de staff
                    </Typography>
                  )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={respuestasAnonimas}
                      onChange={(e) => setRespuestasAnonimas(e.target.checked)}
                      sx={{
                        color: 'primary.main',
                        '&.Mui-checked': { color: 'primary.main' },
                      }}
                    />
                  }
                  label="Respuestas anónimas"
                />
              </Stack>
            )}

            <Stack direction="row" spacing={2}>
              <DateTimePicker
                label="Fecha Límite"
                value={fechaLimite}
                onChange={(newValue) => setFechaLimite(newValue)}
                format="DD/MM/YYYY HH:mm"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DateTimePicker
                label="Fecha Envío Programado"
                value={fechaEnvioProgramada}
                onChange={(newValue) => setFechaEnvioProgramada(newValue)}
                format="DD/MM/YYYY HH:mm"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>

            <Divider>
              <Typography variant="h6" mb="1">Preguntas</Typography>
            </Divider>

            {preguntas.map((pregunta, pIdx) => (
              <Paper
                key={pIdx}
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {/* Header amarillo con título y papelera */}
                <Box
                  sx={{
                    backgroundColor: '#f7d774',
                    px: 2,
                    py: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Pregunta {pregunta.orden}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removePregunta(pIdx)}
                    sx={{
                      color: '#d32f2f',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.2)',
                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Contenido de la pregunta */}
                <Box sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Pregunta *"
                      fullWidth
                      value={pregunta.pregunta}
                      onChange={(e) => updatePregunta(pIdx, { pregunta: e.target.value })}
                    />
                    <Stack direction="row" spacing={2} alignItems="center">
                      <FormControl fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={pregunta.tipo}
                          onChange={(e) => updatePregunta(pIdx, { tipo: e.target.value as any })}
                          label="Tipo"
                        >
                          {TIPOS_PREGUNTA.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={pregunta.requerida}
                            onChange={(e) => updatePregunta(pIdx, { requerida: e.target.checked })}
                            sx={{
                              color: 'primary.main',
                              '&.Mui-checked': { color: 'primary.main' },
                            }}
                          />
                        }
                        label="Obligatoria"
                      />
                    </Stack>

                    {pregunta.tipo === 'puntuacion' && (
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Mínimo"
                        type="number"
                        value={pregunta.escalaMin ?? 1}
                        onChange={(e) => {
                          const val = e.target.value;
                          updatePregunta(pIdx, {
                            escalaMin: val === '' ? undefined : Number(val),
                          });
                        }}
                        inputProps={{ min: 1 }}
                      />
                      <TextField
                        label="Máximo"
                        type="number"
                        value={pregunta.escalaMax ?? 5}
                        onChange={(e) => {
                          const val = e.target.value;
                          updatePregunta(pIdx, {
                            escalaMax: val === '' ? undefined : Number(val),
                          });
                        }}
                        inputProps={{ min: 2 }}
                      />
                    </Stack>
                  )}

                  {(pregunta.tipo === 'texto_corto' || pregunta.tipo === 'texto_largo') && (
                    <TextField
                      label="Límite de caracteres"
                      type="number"
                      value={pregunta.limiteCaracteres ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updatePregunta(pIdx, {
                          limiteCaracteres: val === '' ? undefined : Number(val),
                        });
                      }}
                      inputProps={{ min: 1 }}
                    />
                  )}

                  {(pregunta.tipo === 'seleccion_unica' ||
                    pregunta.tipo === 'seleccion_multiple') && (
                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="subtitle2">Opciones</Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addOpcion(pIdx)}
                        >
                          Agregar Opción
                        </Button>
                      </Stack>
                      {pregunta.opciones?.map((opcion, oIdx) => (
                        <Stack key={oIdx} direction="row" spacing={1} sx={{ mb: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={opcion.texto}
                            onChange={(e) => updateOpcion(pIdx, oIdx, { texto: e.target.value })}
                            placeholder="Texto de la opción"
                          />
                          <IconButton size="small" onClick={() => removeOpcion(pIdx, oIdx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Box>
                  )}
                  </Stack>
                </Box>
              </Paper>
            ))}

            <Button startIcon={<AddIcon />} onClick={addPregunta} variant="outlined">
              Agregar Pregunta
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: '#fff',
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={handleCancelClick}
            disabled={loading}
            sx={{
              color: '#d32f2f',
              '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#21a598',
              '&:hover': { backgroundColor: '#1a8578' },
            }}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación al salir */}
      <ConfirmExitFormularioDialog
        open={confirmExitOpen}
        onClose={() => setConfirmExitOpen(false)}
        onDiscard={handleDiscardChanges}
        onSaveDraft={handleSaveDraft}
        loading={loading}
      />
    </LocalizationProvider>
  );
}
